import { useContext, useMemo } from 'react';
import styled from 'styled-components';

import getIdentifier from '../../../../getIdentifier';
import { BonusOverviewContext } from '../../providers/BonusOverviewProvider';
import useGetFieldLabel from '../../../../hooks/useGetFieldLabel';
import ExpectationList from '../ExpectationList';
import useValueFormatters from '../../../../hooks/useValueFormatters';
import Card from '../../../../kingpin/atoms/Card';
import Loading from '../../../Loading';
import Row from '../../../Common/Row';
import Typography from '../../../../kingpin/atoms/Typography';
import Icon from '../../../../kingpin/atoms/Icon';
import Tooltip from '../../../Tooltip';
import RewardTierTooltip from '../RewardTierTooltip';
import { SubRewardBandTooltip } from '../SubRewardBandSection';
import RewardOverrideControl from '../RewardOverrideControl';
import { isArnoldBrosRewardBands } from '../../utils/rewardBandsUtils';
import isPointsToPayConversionReward from '../../utils/isPointsToPayConversionReward';
import resolveRewardWithCriteria from '../../utils/resolveRewardWithCriteria';

const BodyListDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 8px;
  padding: 16px 24px;
  border: 1px solid var(--color-border-tertiary);
  background-color: var(--color-bg-primary);
`;

// Card wrapping the aggregated reward's sub-reward rows. Spacing/dividers are handled per-row (see
// SubRewardRow) rather than a flex gap, so it mirrors the criteria list (ExpectationList).
const SubRewardListDiv = styled.div`
  width: 100%;
  border-radius: 8px;
  padding: 16px 24px;
  border: 1px solid var(--color-border-tertiary);
  background-color: var(--color-bg-primary);
`;

// A single sub-reward row: label + pay stacked on the left, the override control vertically centred
// on the right, with a divider between rows — matching the criteria row (Expectation, overview).
const SubRewardRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  &:not(:last-of-type) {
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--color-border-tertiary);
  }
`;

export const BonusRewardBody = ({
  bonusProgram,
  criteria,
  variant,
  reward,
  isOverrideMode = false,
  rewardOverrideOptions,
}: {
  bonusProgram: BonusTypes.BonusProgram;
  criteria: BonusTypes.Rewards.CriteriaOutput[];
  variant: 'slideout' | 'overview' | 'tooltip';
  reward?: BonusTypes.Rewards.AnyRewardOutput;
  isOverrideMode?: boolean;
  rewardOverrideOptions?: Map<string, BonusTypes.RewardOverrideOptions>;
}) => {
  const { getFieldLabel } = useGetFieldLabel();
  const { formatField, formatValue } = useValueFormatters();

  const arnoldRewardBands = useMemo(() => {
    // When criteria exist, fall through to ExpectationList so override controls are available.
    if (criteria.length > 0) return undefined;
    if (reward?.rewardBands && isArnoldBrosRewardBands(reward.rewardBands)) {
      return reward.rewardBands;
    }
    return undefined;
  }, [criteria, reward]);

  // A banded reward with no criteria (e.g. tenure `scaledDerivedBanded`, derived purely from a
  // field rather than gated by pass/fail criteria) would otherwise render an empty body. Show the
  // driver's active tier inline (threshold → payout) with an info tooltip holding the full ladder,
  // so the payout isn't left unexplained ("why" behind the amount) without bloating the line.
  const derivedLadder = useMemo(() => {
    if (criteria.length > 0) return undefined;
    const rewardBands = reward?.rewardBands;
    if (!rewardBands || isArnoldBrosRewardBands(rewardBands)) return undefined;
    if (
      reward &&
      isPointsToPayConversionReward({
        reward,
      })
    ) {
      return undefined;
    }
    // For scaled/derived rewards (e.g. tenure) each tier value is a multiplier applied to a $
    // base pay; expose that base so the tooltip can show the driver's math. Final pay is read
    // from the backend `totalPay` in the tooltip — not derived here.
    const isDerived = reward?.type === 'scaledDerivedBanded';
    const base = isDerived ? rewardBands.multiplierFieldValue : undefined;
    return {
      field: rewardBands.field,
      fieldValue: rewardBands.fieldValue,
      bands: rewardBands.bands,
      base,
    };
  }, [criteria, reward]);

  const expectations = useMemo((): {
    key: string;
    field: string;
    label: string;
    isPassed: boolean;
    actual: string;
    target: string;
  }[] => {
    return criteria.map((c) => {
      return {
        key: getIdentifier(undefined, true),
        field: c.name,
        label: getFieldLabel({
          field: c.name,
          dataType: bonusProgram.dataset,
        }),
        isPassed: c.passed,
        actual: formatField({
          value: c.actual || 0,
          field: c.name,
          dataset: bonusProgram.dataset,
        }).toString(),
        target: formatField({
          value: c.target,
          field: c.name,
          dataset: bonusProgram.dataset,
        }).toString(),
      };
    });
  }, [criteria, getFieldLabel, bonusProgram.dataset, formatField]);

  // `aggregated` rewards whose sub-rewards are banded (e.g. Veriha fuel's mpg/idle) get their own
  // per-component breakdown below (each overridable in override mode). This flag keeps them out of
  // the generic rate-based `$ sub-reward list` further down — aggregated rewards with rate-based
  // sub-rewards (e.g. JBC clean inspections) still fall through to that list.
  const hasBandedSubRewards = useMemo(
    () =>
      reward?.type === 'aggregated' &&
      (reward.rewards ?? []).some(
        (sub) => !!sub.rewardBands && !isArnoldBrosRewardBands(sub.rewardBands),
      ),
    [reward],
  );

  // Veriha fuel and similar: an `aggregated` reward's banded components (mpg, idle) are each a
  // full sub-reward. List them so the driver sees the per-component breakdown inline (not just in
  // the heading tooltip), and — in override mode — attach a payout-override control to each, keyed
  // by the sub-reward's own name (the backend returns reward_override_options entries per child).
  const bandedSubRewards = useMemo(() => {
    if (!hasBandedSubRewards) {
      return [];
    }
    return (reward?.rewards ?? []).filter(
      (sub) => !!sub.rewardBands && !isArnoldBrosRewardBands(sub.rewardBands),
    );
  }, [hasBandedSubRewards, reward]);

  if (bandedSubRewards.length > 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SubRewardListDiv>
          {bandedSubRewards.map((subReward) => {
            const bands = subReward.rewardBands;
            // The filter above guarantees a normal (non-Arnold) banded shape, but narrow for TS
            // so we can read the field/value/ladder for the actual value + tier tooltip.
            const normalBands =
              bands && !isArnoldBrosRewardBands(bands) ? bands : undefined;
            const field = normalBands ? normalBands.field : subReward.name;
            const isOverridable = !!rewardOverrideOptions?.get(subReward.name);
            // The driver's actual metric value for this component (e.g. 8.4 mpg) — the piece
            // Mark's review flagged as missing inline. Formatted the same way the aggregated
            // heading tooltip formats it.
            const actualValue = normalBands
              ? formatField({
                  field: normalBands.field,
                  dataset: bonusProgram.dataset,
                  value: normalBands.fieldValue,
                }).toString()
              : undefined;
            const pay =
              bonusProgram.rewardType === 'points'
                ? `${formatValue({ value: subReward.reward ?? 0, formatting: { precision: 0 } })} PTS`
                : formatValue({
                    value: subReward.reward ?? 0,
                    formatting: { prefix: 'currency', precision: 2 },
                  }).toString();
            return (
              <SubRewardRow key={subReward.name}>
                <div>
                  <Typography.Header type={'H3'}>
                    {getFieldLabel({ field, dataType: bonusProgram.dataset })}
                  </Typography.Header>
                  {actualValue !== undefined && (
                    <div data-testid={`sub-reward-actual-${subReward.name}`}>
                      <Typography.Body
                        type={'Label'}
                        color={'var(--color-text-secondary)'}
                      >
                        {actualValue}
                      </Typography.Body>
                    </div>
                  )}
                </div>
                {/* The pay figure with its band-ladder info tooltip trailing it (mirroring the
                    reward heading). The tooltip reuses the same band section as the full aggregated
                    reward tooltip. */}
                <Row centerAlign style={{ gap: 8 }}>
                  {isOverrideMode && isOverridable ? (
                    <RewardOverrideControl
                      rewardName={subReward.name}
                      parentRewardName={reward?.name}
                    />
                  ) : (
                    <Typography.Body type={'Label'}>{pay}</Typography.Body>
                  )}
                  {normalBands && (
                    <Tooltip content={<SubRewardBandTooltip sub={subReward} />}>
                      <Icon
                        icon={'info'}
                        color={'var(--color-icon-secondary)'}
                      />
                    </Tooltip>
                  )}
                </Row>
              </SubRewardRow>
            );
          })}
        </SubRewardListDiv>
        {expectations.length > 0 && (
          <ExpectationList
            expectations={expectations}
            variant={variant}
            rewardName={reward?.name}
          />
        )}
      </div>
    );
  }

  if (arnoldRewardBands) {
    return (
      <BodyListDiv>
        {arnoldRewardBands.components.map((component) => (
          <Row key={component.field} spaceBetween centerAlign>
            <Typography.Body type={'Label'}>
              {getFieldLabel({
                field: component.field,
                dataType: bonusProgram.dataset,
              })}
            </Typography.Body>
            <Typography.Body type={'Label'}>
              {component.fieldValue === null
                ? 'N/A'
                : formatField({
                    field: component.field,
                    dataset: bonusProgram.dataset,
                    value: component.fieldValue,
                  })}
            </Typography.Body>
          </Row>
        ))}
      </BodyListDiv>
    );
  }

  if (derivedLadder) {
    const activeBand = derivedLadder.bands.find((b) => b.active);
    const activeThreshold =
      activeBand?.min ?? activeBand?.max ?? activeBand?.equal;

    // Inline we show just the active tier's threshold; the full ladder (thresholds → payouts)
    // lives behind the info tooltip.
    const activeSummary = formatField({
      field: derivedLadder.field,
      dataset: bonusProgram.dataset,
      value: activeThreshold ?? derivedLadder.fieldValue,
    }).toString();

    const isPassed = !!activeBand;
    const label = getFieldLabel({
      field: derivedLadder.field,
      dataType: bonusProgram.dataset,
    });
    const infoNode = (
      <Tooltip
        content={
          <RewardTierTooltip
            field={derivedLadder.field}
            fieldValue={derivedLadder.fieldValue}
            bands={derivedLadder.bands}
            base={derivedLadder.base}
          />
        }
      >
        <Icon icon={'info'} color={'var(--color-icon-secondary)'} />
      </Tooltip>
    );
    const statusIcon = (
      <Icon
        icon={isPassed ? 'checkmark' : 'cross'}
        color={
          isPassed
            ? 'var(--color-status-success)'
            : 'var(--color-status-danger)'
        }
      />
    );

    // Match the criteria expectation rows so tenure reads consistently: overview stacks the
    // value under an H3 title with the tick on the left; slideout keeps the tick on the right.
    if (variant === 'overview') {
      return (
        <BodyListDiv>
          <Row spaceBetween centerAlign style={{ gap: 16 }}>
            <Row centerAlign style={{ gap: 8 }}>
              <Icon
                icon={isPassed ? 'checkmark' : 'cross'}
                color={
                  isPassed
                    ? 'var(--color-status-success)'
                    : 'var(--color-status-danger)'
                }
                width={18}
                height={18}
              />
              <div>
                <Typography.Header type={'H3'}>{label}</Typography.Header>
                <Typography.Body
                  type={'Label'}
                  color={'var(--color-text-secondary)'}
                >
                  {activeSummary}
                </Typography.Body>
              </div>
            </Row>
            {infoNode}
          </Row>
        </BodyListDiv>
      );
    }

    return (
      <BodyListDiv>
        <Row spaceBetween centerAlign style={{ gap: 16 }}>
          <Row centerAlign style={{ gap: 4 }}>
            <Typography.Body type={'Label'}>{label}</Typography.Body>
            {infoNode}
          </Row>
          <Row centerAlign style={{ gap: 8 }}>
            <Typography.Body type={'Label'}>{activeSummary}</Typography.Body>
            {statusIcon}
          </Row>
        </Row>
      </BodyListDiv>
    );
  }

  const subRewards =
    reward?.rewards && reward.rewards.length > 0 && !hasBandedSubRewards
      ? reward.rewards
      : undefined;

  if (subRewards) {
    return (
      <BodyListDiv>
        {subRewards.map((subReward) => (
          <Row key={subReward.name} spaceBetween centerAlign>
            <Typography.Body type={'Label'}>
              {getFieldLabel({
                field: subReward.name,
                dataType: bonusProgram.dataset,
              })}
            </Typography.Body>
            <Typography.Body type={'Label'}>
              {bonusProgram.rewardType === 'points'
                ? `${formatValue({ value: subReward.reward ?? 0, formatting: { precision: 0 } })} PTS`
                : formatValue({
                    value: subReward.reward ?? 0,
                    formatting: { prefix: 'currency', precision: 2 },
                  })}
            </Typography.Body>
          </Row>
        ))}
      </BodyListDiv>
    );
  }

  // A reward with no criteria, bands, or sub-rewards (e.g. a flat `multiplier` reward like yard
  // fuel — its per-unit math lives in the heading tooltip) has nothing to list; render no box
  // rather than an empty bordered "requirements" container.
  if (expectations.length === 0) {
    return null;
  }

  return (
    <ExpectationList
      expectations={expectations}
      variant={variant}
      rewardName={reward?.name}
    />
  );
};

const Gate = ({ rewardName }: { rewardName: string }) => {
  const { isLoading, bonusOverview, currentMode, rewardOverrideOptions } =
    useContext(BonusOverviewContext);
  const rewardWithCriteria = useMemo(() => {
    if (!bonusOverview) {
      return undefined;
    }
    return resolveRewardWithCriteria({
      rewards: bonusOverview.rewardOutput.rewards,
      criteria: bonusOverview.rewardOutput.criteria,
      rewardName,
    });
  }, [bonusOverview, rewardName]);

  if (isLoading || !bonusOverview) {
    return (
      <Card style={{ height: 56 }}>
        <Loading />
      </Card>
    );
  }

  // Data has loaded but no reward matches this section's name — render nothing rather
  // than a perpetual spinner (mirrors the heading Gate's null-on-miss behaviour).
  if (!rewardWithCriteria) {
    return null;
  }

  return (
    <BonusRewardBody
      criteria={rewardWithCriteria.criteria}
      bonusProgram={bonusOverview.bonusProgram}
      variant={bonusOverview.variant}
      reward={rewardWithCriteria.reward}
      isOverrideMode={currentMode.mode === 'override'}
      rewardOverrideOptions={rewardOverrideOptions}
    />
  );
};

export default Gate;
